// Internal
import {
  AssetDto,
  FIAT_ASSET_IDENTIFIER_KEY,
  FIAT_ASSET_NAME_KEY,
  FIAT_ASSET_SYMBOL_KEY,
  FiatAssetDto,
  NFT_ASSET_COLLECTION_IDENTIFIER_KEY,
  NFT_ASSET_COLLECTION_NAME_KEY,
  NFT_ASSET_IDENTIFIER_KEY,
  NFT_ASSET_IMAGE_KEY,
  NFT_ASSET_NAME_KEY,
  NFT_ASSET_SYMBOL_KEY,
  NftAssetDto,
  TOKEN_ASSET_IDENTIFIER_KEY,
  TOKEN_ASSET_IMAGE_KEY,
  TOKEN_ASSET_NAME_KEY,
  TOKEN_ASSET_SYMBOL_KEY,
  TokenAssetDto,
} from '@/types/assets';
import { Currency, currencyInfo } from '@/types/currency';
import { getChainImageUsingID } from '@/utils/useChains';

// Misc
import BigNumber from 'bignumber.js';
import { get } from 'lodash-es';

export function getAssetIdentifier(
  asset: AssetDto | undefined | null,
  configs?: {
    useNftCollectionId: boolean; // *true* by default
  },
) {
  if (asset?.type === 'fiat') {
    return (asset as FiatAssetDto)[FIAT_ASSET_IDENTIFIER_KEY];
  }

  if (asset?.type === 'token') {
    return (asset as TokenAssetDto)[TOKEN_ASSET_IDENTIFIER_KEY];
  }

  if (asset?.type === 'nft') {
    return get(
      asset as NftAssetDto,
      configs?.useNftCollectionId !== false
        ? NFT_ASSET_COLLECTION_IDENTIFIER_KEY
        : NFT_ASSET_IDENTIFIER_KEY,
    );
  }

  return null;
}

export function getAssetName(asset: AssetDto | undefined | null) {
  if (asset?.type === 'fiat') {
    return get(asset as FiatAssetDto, FIAT_ASSET_NAME_KEY) as string;
  }

  if (asset?.type === 'token') {
    return get(asset as TokenAssetDto, TOKEN_ASSET_NAME_KEY) as string;
  }

  if (asset?.type === 'nft') {
    return get(asset as NftAssetDto, NFT_ASSET_NAME_KEY) as string;
  }

  return null;
}

export function getAssetSymbol(asset: AssetDto | undefined | null) {
  if (asset?.type === 'fiat') {
    return (asset as FiatAssetDto)[FIAT_ASSET_SYMBOL_KEY] as string;
  }

  if (asset?.type === 'token') {
    return (asset as TokenAssetDto)[TOKEN_ASSET_SYMBOL_KEY] as string;
  }

  if (asset?.type === 'nft') {
    return (
      get(asset as NftAssetDto, NFT_ASSET_SYMBOL_KEY) ||
      get(asset as NftAssetDto, NFT_ASSET_COLLECTION_NAME_KEY)
    );
  }

  return get(asset, 'symbol') ?? null;
}

export function getAssetImageUrl(
  asset: AssetDto | undefined | null,
  { chain }: { chain?: string },
) {
  if (asset?.type === 'fiat') {
    return null;
  }

  if (asset?.type === 'token') {
    return (
      (get(asset as TokenAssetDto, TOKEN_ASSET_IMAGE_KEY) as string) ||
      (chain ? getChainImageUsingID(chain) : null)
    );
  }

  if (asset?.type === 'nft') {
    return get(asset as NftAssetDto, NFT_ASSET_IMAGE_KEY) as string;
  }

  return null;
}

export function isSameAsset(
  asset1: AssetDto | undefined | null,
  asset2: AssetDto | undefined | null,
  configs?: {
    useNftCollectionId: boolean; // *true* by default
  },
) {
  if (!asset1 || !asset2) return false;

  if (asset1.type !== asset2.type) return false;

  if (asset1.type === 'fiat') {
    return (
      (asset1 as FiatAssetDto)[FIAT_ASSET_IDENTIFIER_KEY] ===
      (asset2 as FiatAssetDto)[FIAT_ASSET_IDENTIFIER_KEY]
    );
  }

  if (asset1.type === 'token') {
    return (
      (asset1 as TokenAssetDto)[TOKEN_ASSET_IDENTIFIER_KEY] ===
      (asset2 as TokenAssetDto)[TOKEN_ASSET_IDENTIFIER_KEY]
    );
  }

  if (asset1.type === 'nft') {
    return (
      get(
        asset1 as NftAssetDto,
        configs?.useNftCollectionId !== false
          ? NFT_ASSET_COLLECTION_IDENTIFIER_KEY
          : NFT_ASSET_IDENTIFIER_KEY,
      ) ===
      get(
        asset2 as NftAssetDto,
        configs?.useNftCollectionId !== false
          ? NFT_ASSET_COLLECTION_IDENTIFIER_KEY
          : NFT_ASSET_IDENTIFIER_KEY,
      )
    );
  }

  return false;
}

export enum ASSET_FORMATTING_SIGN_LOGIC {
  NONE = 'none', // no signs or brackets
  NEGATIVE_ONLY = 'negative-only', // **default**
  POSITIVE_AND_NEGATIVE = 'positive-and-negative',
  BRACKETS_ONLY = 'brackets', // brackets for negative values
}

export type AssetFormatterConfigs = {
  includeSymbol?: boolean;
  signLogic?: ASSET_FORMATTING_SIGN_LOGIC; // whether to show brackets,
  symbolPlacement?: 'start' | 'end'; // defaults to `start`
  // negatives only, positives only etc...
  sign?: string; // The sign to be displayed. If not provided,
  // it will be determined based on the amount.
  treatAssetAmountsAsNegative?: boolean;
  treatAssetAmountsNanAsZero?: boolean;
  // indicate decimal places for zero amounts
  reserveDecimalsForZeroAssetAmounts?: boolean;
};

export interface AssetFormattingResult {
  sign: string;
  formattedString: string;
  formattedStringWithoutSign: string;
}

/**
 * Formats the asset amount to a string based on the provided asset and configurations.
 * @param asset - The asset to be formatted.
 * @param configs - The formatting configurations.
 * @returns The formatted asset amount string.
 */
export const formatAssetAmount = (
  asset: AssetDto | undefined | null,
  configs?: AssetFormatterConfigs,
): string | undefined | null => {
  if (!asset || asset.amount === undefined || asset.amount === null) return null;

  if (Number.isNaN(Number(asset?.amount))) {
    if (configs?.treatAssetAmountsNanAsZero) {
      asset.amount = '0';
    }
    return null;
  }

  const parsedAmount = new BigNumber(asset.amount);
  return (
    applyFormattingToParsedAmount({
      type: asset.type,
      parsedAmount,
      precision: asset.precision,
      symbol: getAssetSymbol(asset),
      configs,
    })?.formattedString || null
  );
};

/**
 * Formats the total amount of an array of assets to a string based on the provided configurations.
 * @param assetArray - The array of assets.
 * @param configs - The formatting configurations.
 * @returns The formatted total asset amount string.
 */
export function formatAssetArrayTotalAmount(
  assetArray: AssetDto[] | undefined | null,
  configs?: AssetFormatterConfigs,
) {
  // Guard against `null`, `undefined`, or empty array
  if (!Array.isArray(assetArray) || assetArray.length === 0) {
    return null;
  }

  let _parsedAmount = assetArray.reduce(
    (accumulator, currentValue) =>
      new BigNumber(accumulator).plus(new BigNumber(currentValue.amount)),
    new BigNumber(0),
  );

  if (configs?.treatAssetAmountsAsNegative) {
    _parsedAmount = _parsedAmount.negated();
  }

  // NOTE: assumes precision, symbol, and type are the same for all assets in the array
  const type = assetArray[0].type;
  const { precision } = assetArray[0];
  const symbol = getAssetSymbol(assetArray[0]);

  return applyFormattingToParsedAmount({
    type,
    parsedAmount: _parsedAmount,
    precision,
    symbol,
    configs,
  })?.formattedString;
}

/**
 * Helper function for `formatAssetAmount` and `formatAssetArrayTotalAmount`. SH
 * Applies formatting to a parsed asset amount based on the provided parameters.
 *
 * @param type - The type of the asset.
 * @param parsedAmount - The parsed amount of the asset as a BigNumber.
 * @param precision - The precision of the asset.
 * @param symbol - The symbol of the asset.
 * @param configs - Optional configurations for the asset formatter.
 * @returns The formatted amount as a string.
 */
export const applyFormattingToParsedAmount = ({
  type,
  parsedAmount,
  precision,
  symbol,
  configs,
}: {
  type: AssetDto['type'];
  parsedAmount: BigNumber;
  precision: AssetDto['precision'];
  symbol: string | null;
  configs?: AssetFormatterConfigs;
}): AssetFormattingResult => {
  let formattedString: string;
  let formattedStringWithoutSign: string;
  let sign = '';

  // Use `precision` parameter ONLY as a maximum, not as an amount to `always` apply
  const applicablePrecision = (() => {
    if (type === 'fiat' || configs?.reserveDecimalsForZeroAssetAmounts) {
      return precision;
    }

    const _maxPrecisionNeeded = Math.min(parsedAmount.decimalPlaces() || 0, precision);
    const _roundedNumber = BigNumber(parsedAmount.toFixed(_maxPrecisionNeeded));

    return Math.min(_maxPrecisionNeeded, _roundedNumber.decimalPlaces() || 0);
  })();

  // Determine the sign to be displayed
  if (configs?.signLogic !== ASSET_FORMATTING_SIGN_LOGIC.NONE) {
    if (configs?.sign) {
      sign = configs?.sign;
    } else {
      if (configs?.treatAssetAmountsAsNegative && !parsedAmount.isZero()) {
        if (parsedAmount.isGreaterThan(0)) {
          parsedAmount = parsedAmount.negated();
        }

        sign = '-';
      } else {
        if (
          parsedAmount.isNegative() &&
          (!configs?.signLogic ||
            [
              ASSET_FORMATTING_SIGN_LOGIC.NEGATIVE_ONLY,
              ASSET_FORMATTING_SIGN_LOGIC.POSITIVE_AND_NEGATIVE,
            ].includes(configs?.signLogic))
        ) {
          sign = '-';
        } else if (
          parsedAmount.isGreaterThan(0) &&
          configs?.signLogic &&
          [ASSET_FORMATTING_SIGN_LOGIC.POSITIVE_AND_NEGATIVE].includes(configs?.signLogic)
        ) {
          sign = '+';
        }
      }
    }
  }

  // Determine whether to use absolute value or not
  if (
    [ASSET_FORMATTING_SIGN_LOGIC.NONE, ASSET_FORMATTING_SIGN_LOGIC.BRACKETS_ONLY].includes(
      configs?.signLogic as ASSET_FORMATTING_SIGN_LOGIC,
    ) ||
    sign
  ) {
    formattedString = parsedAmount.absoluteValue().toFormat(applicablePrecision);
  } else {
    formattedString = parsedAmount.toFormat(applicablePrecision);
  }

  // Store the formatted string without the sign
  formattedStringWithoutSign = parsedAmount.absoluteValue().toFormat(applicablePrecision);

  // If the amount is `0`, then don't apply any formatting related to rounding
  if (parsedAmount.isEqualTo(0) && type === 'fiat') {
    formattedString = parsedAmount.toFixed(2);
    formattedStringWithoutSign = formattedString;

    // If sign should show, then prepend it to the formatted string
    if (configs?.signLogic !== ASSET_FORMATTING_SIGN_LOGIC.BRACKETS_ONLY) {
      formattedString = `${sign}${formattedString}`;
    }
  } else if (parsedAmount.isEqualTo(0) && type !== 'fiat') {
    formattedString = parsedAmount.toFixed(applicablePrecision);
    formattedStringWithoutSign = formattedString;

    // If sign should show, then prepend it to the formatted string
    if (configs?.signLogic !== ASSET_FORMATTING_SIGN_LOGIC.BRACKETS_ONLY) {
      formattedString = `${sign}${formattedString}`;
    }
  } else if (!parsedAmount.isEqualTo(0)) {
    // If the amount was rounded up, then display the less than (`<`) symbol (assuming it's absolute value is `<1`)
    // i.e. the original amount will be less than the formatted amount to be rendered
    const minAmountForPrecision = new BigNumber(10).pow(-precision);
    if (parsedAmount.abs().isLessThan(minAmountForPrecision)) {
      formattedString = `<${minAmountForPrecision.toFixed()}`;

      // Store the formatted string without the sign
      formattedStringWithoutSign = formattedString;
    }
  }

  // Brackets
  if (
    parsedAmount.isNegative() &&
    configs?.signLogic === ASSET_FORMATTING_SIGN_LOGIC.BRACKETS_ONLY
  ) {
    formattedString = `(${formattedString})`;
  }

  // Symbol
  if (configs?.includeSymbol && symbol) {
    formattedString = (
      configs?.symbolPlacement === 'end' ? [formattedString, symbol] : [symbol, formattedString]
    ).join(type === 'fiat' ? '' : ' ');
  }

  // If sign should show, then prepend it to the formatted string
  if (configs?.signLogic !== ASSET_FORMATTING_SIGN_LOGIC.BRACKETS_ONLY) {
    formattedString = `${sign}${formattedString}`;
  }

  return { formattedString, formattedStringWithoutSign, sign };
};

/**
 * Returns a standard USD fiat asset object.
 * Useful for older endpoints that are not conformed to our `AssetDto` type.
 *
 * @param amount - The amount of the asset.
 * @param decimal - The precision of the asset amount (optional, defaults to 2).
 * @returns The fiat asset object or undefined if amount is undefined, null if amount is null.
 */
export const getStandardUsdFiatAsset = (
  amount: string | number | undefined | null,
  decimal?: number,
): FiatAssetDto | undefined | null => {
  if (amount === undefined || amount === null) {
    return amount;
  }

  if (typeof amount === 'number') {
    amount = amount.toString();
  }

  return {
    type: 'fiat',
    amount: amount,
    precision: decimal ?? getDefaultTokenAssetPrecision(Currency.USD) ?? 2,
    ticker: Currency.USD,
    symbol: currencyInfo.USD.symbol,
    name: currencyInfo.USD.name,
  };
};

/**
 * Returns the default precision for a token asset based on its ticker.
 * Useful for older endpoints that are not conformed to our `AssetDto` type.
 *
 * @param ticker The ticker symbol of the token asset.
 * @returns The default precision for the token asset.
 */
export const getDefaultTokenAssetPrecision = (ticker: TokenAssetDto['ticker']) => {
  switch (ticker) {
    case 'ETH':
      return 6;
    case 'USDC':
    case 'USDT':
    case Currency.USD:
      return 2;
    default:
      return null;
  }
};
